import React, {useRef, useState} from 'react';
import {Divider, Form, Steps} from 'antd';
import SelectContent from "./steps/SelectContent";
import ChooseResources from "./steps/ChooseResources";
import ConfirmSelection from "./steps/ConfirmSelection";
import LessonPlanGenerating from "../LessonPlanGenerating";
import {useBreadcrumbComponent} from "@common/useBreadcrumbComponent";
import Button from "@common/Button";
import {ArrowBackRounded, ArrowForwardRounded, AutoAwesomeRounded} from '@mui/icons-material';
import useCurrentShortcut from "@hooks/useCurrentShortcut";
import axios from "@common/axios";

const GenerateButton = ({ className, isLoading }) => {
    const reviewSelections = Form.useWatch('reviewSelections');
    const hasReviewSelection = !reviewSelections || Object.values(reviewSelections).some(Boolean);
    return (
        <Button
            type="primary"
            htmlType="submit"
            size="large"
            icon={<AutoAwesomeRounded />}
            iconPosition="right"
            className={className}
            disabled={!hasReviewSelection}
            isLoading={isLoading}
        >
            Generate Lesson Plan
        </Button>
    );
};

const LessonPlannerWizard = () => {
    const BreadcrumbBar = useBreadcrumbComponent({ dropEndingSeparator: true });
    const [currentStep, setCurrentStep] = useState(0);
    const [form] = Form.useForm();
    const [selectedConcepts, setSelectedConcepts] = useState({});
    const [selectedResources, setSelectedResources] = useState({});
    const [reviewSelections, setReviewSelections] = useState({});
    const [generating, setGenerating] = useState(false);
    const [planReady, setPlanReady] = useState(false);
    const planIdRef = useRef(null);
    const [selectContentError, setSelectContentError] = useState(false);
    const { loadShortcutByUrl } = useCurrentShortcut();

    // Read lesson ID from URL if provided (launched from lesson scheduler)
    const params = new URLSearchParams(window.location.search);
    const sourceLessonId = params.get('lessonId') ? parseInt(params.get('lessonId')) : null;

    const steps = [
        { title: 'Select Content',    content: <SelectContent showError={selectContentError} /> },
        { title: 'Choose Resources',  content: <ChooseResources /> },
        { title: 'Confirm Selection', content: <ConfirmSelection selectedResources={selectedResources} selectedConcepts={selectedConcepts} onSelectionsChange={setReviewSelections} /> },
    ];

    const handleNext = () => {
        if (currentStep === 0) {
            const conceptsValue = form.getFieldValue('selectedConcepts');
            const hasSelection = conceptsValue && Object.values(conceptsValue).some(Boolean);
            if (!hasSelection) {
                setSelectContentError(true);
                return;
            }
            setSelectContentError(false);
            setSelectedConcepts(conceptsValue);
        }
        if (currentStep === 1) {
            setSelectedResources(form.getFieldValue('selectedResources') || {});
        }
        if (currentStep === 2) {
            // Capture review selections before the component unmounts
            const selections = form.getFieldValue('reviewSelections') || {};
            console.log('Saving reviewSelections to wizard state:', selections);
            setReviewSelections(selections);
        }
        setCurrentStep(s => s + 1);
    };
    const handlePrev = () => setCurrentStep(s => s - 1);

    const handleFinish = (values) => {
        console.log('handleFinish called with values:', values);
        console.log('values.selectedConcepts:', values.selectedConcepts);
        console.log('State selectedConcepts:', selectedConcepts);
        console.log('values.reviewSelections:', values.reviewSelections);
        console.log('All form values:', form.getFieldsValue(true));
        setGenerating(true);

        // Extract selected concepts from state (not form values, since the step is unmounted)
        const conceptIds = Object.keys(selectedConcepts)
            .filter(id => selectedConcepts[id])
            .map(id => parseInt(id));

        console.log('Selected concept IDs:', conceptIds);

        if (conceptIds.length === 0) {
            displayToastMessage('No content selected', 'Please select at least one section', 'warning');
            setGenerating(false);
            return;
        }

        // Use first concept for now (can be enhanced to support multiple)
        const firstConceptId = conceptIds[0];

        // Build list of selected resource types
        const resourceTypes = Object.keys(selectedResources)
            .filter(key => selectedResources[key])
            .map(key => key.toUpperCase()); // Convert to uppercase for backend enum

        // Build config
        const config = {
            conceptId: firstConceptId,
            lessonPlanTemplateId: 'default',
            durationMinutes: 60,
            selectedResources: resourceTypes
        };

        // Get review selections from wizard state (not form values, as Step 3 component is unmounted)
        // Format: { "TUTORIAL:123": true, "MEDIA:456": true, ... }
        console.log('Review selections from wizard state:', reviewSelections);
        console.log('Review selections from form values:', values.reviewSelections);

        // Call generation API
        console.log('Calling API with config:', config);
        console.log('Selected resources:', selectedResources);
        console.log('Resource types being sent:', resourceTypes);

        axios.post('/web/ui/lessonPlanner/generateLessonPlan', config)
            .then(response => {
                console.log('API response:', response.data);
                const lessonPlanId = response.data.lessonPlanId;
                planIdRef.current = lessonPlanId ?? null;

                // Save selected resources from Step 3
                // Parse reviewSelections format: "TUTORIAL:123" -> { resourceType: "TUTORIAL", resourceId: 123 }
                const selectedResourcesList = Object.entries(reviewSelections)
                    .filter(([key, checked]) => checked)
                    .map(([key, _]) => {
                        const [resourceType, resourceId] = key.split(':');

                        // Determine section key based on resource type
                        // This helps organize resources into appropriate sections
                        let sectionKey = 'extras';
                        if (resourceType === 'TUTORIAL') {
                            sectionKey = 'textbookContent';
                        } else if (resourceType === 'QUESTION_CLUSTER') {
                            sectionKey = 'quiz';
                        } else if (resourceType === 'QUESTION') {
                            sectionKey = 'walkthroughs';
                        } else if (resourceType === 'MEDIA') {
                            sectionKey = 'videos'; // Default for media, could be worksheets/extras too
                        }

                        return {
                            resourceType,
                            resourceId: parseInt(resourceId),
                            sectionKey,
                            selected: true,
                            topPick: false,
                        };
                    });

                console.log('Saving resources:', selectedResourcesList);

                // Save all resources and link to lesson if applicable
                const saveResourcesPromise = selectedResourcesList.length > 0 && lessonPlanId
                    ? axios.post(`/web/ui/lessonPlanner/lessonPlans/${lessonPlanId}/resources/bulk`, selectedResourcesList)
                        .then(response => {
                            const { successCount, totalCount } = response.data;
                            console.log(`Saved ${successCount}/${totalCount} resources via bulk endpoint`);
                        })
                        .catch(error => {
                            console.error('Error saving resources:', error);
                        })
                    : Promise.resolve();

                // Link to source lesson if provided
                const linkLessonPromise = sourceLessonId && lessonPlanId
                    ? axios.post(`/web/ui/lessonPlanner/lessons/${sourceLessonId}/linkLessonPlan/${lessonPlanId}`)
                        .then(() => {
                            console.log(`Linked lesson ${sourceLessonId} to lesson plan ${lessonPlanId}`);
                        })
                        .catch(error => {
                            console.error('Error linking lesson to lesson plan:', error);
                            // Non-fatal error, continue anyway
                        })
                    : Promise.resolve();

                // Wait for both operations to complete
                Promise.all([saveResourcesPromise, linkLessonPromise])
                    .finally(() => {
                        // Signal the loading screen — it will navigate once its animation finishes
                        setPlanReady(true);
                    });
            })
            .catch(error => {
                console.error('API error:', error);
                setGenerating(false);
                setPlanReady(false);

                let errorMessage = 'Failed to create lesson plan';
                if (error.response?.data?.message) {
                    errorMessage = error.response.data.message;
                } else if (error.response?.data?.error) {
                    errorMessage = error.response.data.error;
                } else if (error.response?.data) {
                    errorMessage = JSON.stringify(error.response.data);
                }

                displayToastMessage('Creation failed', errorMessage, 'error');
            });
    };

    const handleGenerationDone = () => {
        if (planIdRef.current) {
            loadShortcutByUrl(`/web/ui/view/lessonStudio/lessonPlans/plan/${planIdRef.current}`);
        } else {
            loadShortcutByUrl('/web/ui/view/lessonStudio/lessonPlans');
        }
    };

    if (generating) {
        return (
            <LessonPlanGenerating
                isComplete={planReady}
                onDone={handleGenerationDone}
            />
        );
    }

    return (
        <div>
            {BreadcrumbBar && <BreadcrumbBar />}
            <Form form={form} layout="vertical" onFinish={handleFinish} className={'ch-form ch-lesson-planner-wizard-form'}>
                <Steps
                    current={currentStep}
                    items={steps.map(s => ({ title: s.title }))}
                />
                {steps[currentStep].content}
                <Divider className={'ch-divider'} />
                <div className={'ch-lesson-planner-stepper-buttons'}>
                    {currentStep > 0 &&
                        <Button htmlType="button" type="outline" onClick={handlePrev} size="large" icon={<ArrowBackRounded />}>Back</Button>
                    }
                    {currentStep < steps.length - 1 &&
                        <Button
                            htmlType="button"
                            type="primary"
                            onClick={handleNext}
                            size="large"
                            icon={<ArrowForwardRounded />}
                            iconPosition="right"
                            className={'ch-lesson-planner-stepper-next'}
                        >
                            Next
                        </Button>
                    }
                    {currentStep === steps.length - 1 &&
                        <GenerateButton className={'ch-lesson-planner-stepper-next'} isLoading={false} />
                    }
                </div>
            </Form>
        </div>
    );
};

export default LessonPlannerWizard;
